import type { ReactNode } from "react";
import { Box } from "@mui/material";
import { formatDataBR } from "@/api/escala.service";
import type { DiaColuna } from "@/components/escala/espelho";
import { COR_FERIADO } from "@/components/escala/tiposDia";
import {
  ALTURA_CABECALHO,
  LARGURA_DIA,
  LARGURA_TOTAL,
  celulaBase,
  faixa,
  fixaDireita,
  historicoAtenuado,
  separadorHistorico,
  fixaEsquerda,
  larguraColaborador,
  rotuloCaixaAlta,
} from "@/components/escala/espelhoEstilos";

const cabecalho = { position: "sticky", top: 0, height: ALTURA_CABECALHO, bgcolor: "background.paper" } as const;

export interface ColunaTotal {
  rotulo: string;
  titulo: string;
}

export const TOTAIS_ESPELHO: ColunaTotal[] = [
  { rotulo: "TT", titulo: "Dias trabalhados" },
  { rotulo: "TF", titulo: "Folgas" },
];

// Só na escala com carga horária, depois do TF.
export const TOTAL_HORAS: ColunaTotal = { rotulo: "TH", titulo: "Horas trabalhadas no mês, sem o intervalo" };

interface EspelhoCabecalhoProps {
  colunas: DiaColuna[];
  // Dias do mês anterior, antes do dia 1 (só no espelho).
  colunasHistorico?: DiaColuna[];
  rotulo?: string;
  totais?: ColunaTotal[];
  // Vai dentro do cabeçalho da coluna do nome: a borda que ajusta a largura (só no espelho).
  alcaColaborador?: ReactNode;
}

export function EspelhoCabecalho({
  colunas,
  colunasHistorico = [],
  rotulo = "Colaborador",
  totais = TOTAIS_ESPELHO,
  alcaColaborador,
}: EspelhoCabecalhoProps) {
  return (
    <thead>
      <Box component="tr">
        <Box
          component="th"
          scope="col"
          sx={{
            ...celulaBase,
            ...cabecalho,
            ...fixaEsquerda,
            zIndex: 5,
            width: larguraColaborador(),
            minWidth: larguraColaborador(),
            pl: 2.5,
            textAlign: "left",
            ...rotuloCaixaAlta,
          }}
        >
          {rotulo}
          {alcaColaborador}
        </Box>
        {colunasHistorico.map((c, i) => (
          <Box
            component="th"
            scope="col"
            key={`h-${c.iso}`}
            title={`Mês anterior — ${formatDataBR(c.iso)}`}
            sx={[
              celulaBase,
              cabecalho,
              historicoAtenuado,
              i === colunasHistorico.length - 1 && separadorHistorico,
              { zIndex: 3, width: LARGURA_DIA, minWidth: LARGURA_DIA, lineHeight: 1.2 },
              faixa(c),
            ]}
          >
            <Box sx={{ fontSize: 10, fontWeight: 600, color: c.domingo ? "primary.main" : "text.secondary" }}>{c.letra}</Box>
            <Box sx={{ mt: 0.25, fontSize: 12, fontWeight: 700, lineHeight: "24px" }}>{c.dia}</Box>
          </Box>
        ))}
        {colunas.map((c) => {
          const especial = c.domingo || !!c.feriado;
          return (
            <Box
              component="th"
              scope="col"
              key={c.dia}
              title={c.feriado ? `${c.feriado} — ${formatDataBR(c.iso)}` : undefined}
              sx={[
                celulaBase,
                cabecalho,
                { zIndex: 3, width: LARGURA_DIA, minWidth: LARGURA_DIA, lineHeight: 1.2 },
                faixa(c),
              ]}
            >
              <Box sx={{ fontSize: 10, fontWeight: 600, color: c.feriado ? COR_FERIADO : especial ? "primary.main" : "text.secondary" }}>
                {c.letra}
              </Box>
              <Box
                sx={{
                  mx: "auto",
                  mt: 0.25,
                  width: 24,
                  height: 24,
                  lineHeight: "24px",
                  borderRadius: "50%",
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: c.feriado ? COR_FERIADO : especial ? "primary.main" : "transparent",
                  color: especial ? "primary.contrastText" : "text.primary",
                }}
              >
                {c.dia}
              </Box>
            </Box>
          );
        })}
        {totais.map((total, i) => (
          <Box
            component="th"
            scope="col"
            key={total.rotulo}
            title={total.titulo}
            sx={{
              ...celulaBase,
              ...cabecalho,
              ...fixaDireita((totais.length - 1 - i) * LARGURA_TOTAL),
              zIndex: 5,
              width: LARGURA_TOTAL,
              minWidth: LARGURA_TOTAL,
              ...rotuloCaixaAlta,
              borderLeft: i === 0 ? "1px solid" : "none",
              borderLeftColor: "divider",
            }}
          >
            {total.rotulo}
          </Box>
        ))}
      </Box>
    </thead>
  );
}
